import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useCurrentDraw, useDrawEligibility } from "../hooks/useDrawData";
import styles from "../styles/FloatingDrawButton.module.css";
import { useAuth } from "../contexts/AppKitAuthContext";

interface FloatingDrawButtonProps {
  hasActiveGame?: boolean;
}

const FloatingDrawButton: React.FC<FloatingDrawButtonProps> = ({ hasActiveGame = false }) => {
  const navigate = useNavigate();
  const [showNotification, setShowNotification] = useState(false);

  const { currentDraw, refetch } = useCurrentDraw();
  const { eligibility, checkEligibility } = useDrawEligibility();
  const { isAuthenticated } = useAuth();

  const isAuthenticatedRef = useRef(isAuthenticated);
  useEffect(() => {
    // Refresh data when user signs in (auth changes from false to true)
    if (!isAuthenticatedRef.current && isAuthenticated) {
      checkEligibility();
      refetch();
    }
    isAuthenticatedRef.current = isAuthenticated;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Show notification if user is eligible but hasn't participated
  useEffect(() => {
    if (isAuthenticated && eligibility?.isEligible && currentDraw && !currentDraw.userParticipation) {
      setShowNotification(true);

      // Hide notification after 10 seconds
      const timer = setTimeout(() => setShowNotification(false), 10000);
      return () => clearTimeout(timer);
    } else {
      setShowNotification(false);
    }
  }, [eligibility, currentDraw, isAuthenticated]);

  const handleClick = () => {
    navigate("/draw");
  };

  const getButtonState = () => {
    if (!isAuthenticated) {
      return {
        icon: "🎲",
        text: "Daily Free Draw",
        status: "unauthenticated",
        pulse: false,
      };
    }

    if (!currentDraw) {
      return {
        icon: "🎲",
        text: "Daily Free Draw",
        status: "loading",
        pulse: false,
      };
    }

    if (currentDraw.userParticipation) {
      return {
        icon: "✅",
        text: `Joined #${currentDraw.userParticipation.seqNumber}`,
        status: "participated",
        pulse: false,
      };
    }

    if (eligibility?.isEligible) {
      return {
        icon: "🎯",
        text: "Join Free Draw!",
        status: "eligible",
        pulse: true,
      };
    }

    return {
      icon: "🎲",
      text: "Daily Free Draw",
      status: "default",
      pulse: false,
    };
  };

  const buttonState = getButtonState();

  // Hide the button if there's an active game
  if (hasActiveGame) {
    return null;
  }

  return (
    <>
      <div
        className={`${styles.floatingDrawButton} ${isAuthenticated ? styles.authenticated : ""} ${
          styles[buttonState.status]
        } ${buttonState.pulse ? styles.pulse : ""}`}
        onClick={handleClick}
        title="DXF Daily Draw - Daily draws with token rewards!"
      >
        <div className={styles.buttonIcon}>{buttonState.icon}</div>
        <div className={styles.buttonText}>{buttonState.text}</div>

        {currentDraw && <div className={styles.participantCount}>{currentDraw.participantCount} Joined Today</div>}
      </div>

      {showNotification && (
        <div className={styles.drawNotification}>
          <div className={styles.notificationContent}>
            <div className={styles.notificationIcon}>🎯</div>
            <div className={styles.notificationText}>
              <div className={styles.notificationTitle}>You're eligible for today's draw!</div>
              <div className={styles.notificationSubtitle}>
                <a href="/draw">Click to join</a> and win 100 DXF tokens
              </div>
            </div>
            <button className={styles.notificationClose} onClick={() => setShowNotification(false)}>
              ×
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default FloatingDrawButton;
